'use strict';

import { CONFIG } from '../core/config.js';

/**
 * Keys this manager reacts to. Anything else falls through to the browser, so
 * Tab, Shift+Tab, and Space keep their native behavior.
 */
const KEYS = {
	ARROW_DOWN: 'ArrowDown',
	ARROW_UP: 'ArrowUp',
	END: 'End',
	ENTER: 'Enter',
	ESCAPE: 'Escape',
	HOME: 'Home',
};

/**
 * Keyboard Navigation Manager
 *
 * Layers arrow-key navigation onto the dropdowns without taking anything away
 * from the native tab order — both routes stay available, so a keyboard user can
 * Tab between fields and arrow through the options within one.
 *
 * - Trigger: Down/Up opens the dropdown and lands on its first/last option.
 * - Options: Down/Up move one row (wrapping); Home/End jump to the ends.
 * - Options: Enter selects the row, matching a click. From the search input it
 *   selects the first remaining match. Without this, Enter on a checkbox or a
 *   text field implicitly submits the form while the dropdown is still open.
 * - Buttons: Enter is left native, so Done and a row's deselect (×) activate
 *   themselves rather than being read as a press on the row around them.
 * - Open dropdown: Escape closes it and returns focus to the trigger, whether
 *   focus is inside the modal or still on the trigger that opened it.
 */
export class KeyboardManager {
	constructor(modalManager, callbacks = {}) {
		this.modalManager = modalManager;
		this.callbacks = {
			trackDropdownOpen: callbacks.trackDropdownOpen || (() => {}),
		};
	}

	/**
	 * Route a keydown to the trigger or the open-dropdown handler.
	 *
	 * @param {KeyboardEvent} e - The keydown event.
	 * @return {void}
	 */
	handleKeydown(e) {
		if (e.defaultPrevented || typeof e.target?.closest !== 'function') {
			return;
		}

		const trigger = e.target.closest(CONFIG.ELEMENTS.modalTrigger);
		if (trigger) {
			this.handleTriggerKeydown(e, trigger);
			return;
		}

		const modal = e.target.closest(CONFIG.ELEMENTS.dropdownModal);
		if (modal) {
			this.handleModalKeydown(e, modal);
		}
	}

	/**
	 * Handle keys pressed on a dropdown trigger: Down/Up open the dropdown (Enter
	 * and Space already do that natively) and move focus straight into the options
	 * list, and Escape closes a dropdown that is already open.
	 *
	 * @param {KeyboardEvent} e       - The keydown event.
	 * @param {HTMLElement}   trigger - The dropdown trigger button.
	 * @return {void}
	 */
	handleTriggerKeydown(e, trigger) {
		const dropdown = trigger.closest(CONFIG.ELEMENTS.dropdown);
		const modal = dropdown?.querySelector(CONFIG.ELEMENTS.dropdownModal);
		if (!modal) {
			return;
		}

		// A click leaves focus on the trigger with the dropdown open, so Escape has
		// to be handled here as well as inside the modal. Left native when there is
		// nothing open to close.
		if (e.key === KEYS.ESCAPE) {
			if (this.modalManager.getOpenModal(dropdown)) {
				e.preventDefault();
				this.modalManager.close(modal);
			}
			return;
		}

		if (e.key !== KEYS.ARROW_DOWN && e.key !== KEYS.ARROW_UP) {
			return;
		}

		// Stop the arrow key from scrolling the page behind the dropdown.
		e.preventDefault();

		if (!this.modalManager.getOpenModal(dropdown)) {
			// Via toggle() so the open sequence has a single owner in ModalManager.
			this.modalManager.toggle(modal, dropdown);
			this.callbacks.trackDropdownOpen(trigger);
		}

		const options = this.getAvailableOptions(modal);
		this.focusOption(
			e.key === KEYS.ARROW_UP ? options[options.length - 1] : options[0],
		);
	}

	/**
	 * Handle keys pressed inside an open dropdown — on an option row, the search
	 * input, or the Done button.
	 *
	 * @param {KeyboardEvent} e     - The keydown event.
	 * @param {HTMLElement}   modal - The dropdown modal holding focus.
	 * @return {void}
	 */
	handleModalKeydown(e, modal) {
		if (e.key === KEYS.ESCAPE) {
			e.preventDefault();
			this.modalManager.close(modal);
			return;
		}

		const option = e.target.closest(CONFIG.ELEMENTS.option);
		const isSearchInput = e.target.matches(CONFIG.ELEMENTS.searchInput);

		// A button already activates itself on Enter, and both of the modal's buttons
		// are `type="button"`, so neither one submits the form. Leave them to the
		// browser: Done closes the dropdown, and the deselect (×) inside a row is the
		// only route to clearing a single-select choice.
		const isButton = !!e.target.closest('button');

		if (e.key === KEYS.ENTER && !isButton && (option || isSearchInput)) {
			// A focused checkbox and a focused text field, on the other hand, both
			// submit their form on Enter in most browsers, which would navigate away
			// mid-selection. Select instead: the focused row, or from the search input
			// the first row still matching what was typed.
			e.preventDefault();
			const target = option ?? this.getAvailableOptions(modal)[0];
			target?.querySelector(CONFIG.ELEMENTS.input)?.click();
			return;
		}

		if (e.key === KEYS.ARROW_DOWN || e.key === KEYS.ARROW_UP) {
			e.preventDefault();
			this.moveFocus(modal, option, e.key === KEYS.ARROW_UP ? -1 : 1);
			return;
		}

		// Home/End belong to the caret while the user is typing a search term.
		if ((e.key === KEYS.HOME || e.key === KEYS.END) && !isSearchInput) {
			e.preventDefault();
			const options = this.getAvailableOptions(modal);
			this.focusOption(
				e.key === KEYS.HOME ? options[0] : options[options.length - 1],
			);
		}
	}

	/**
	 * Move focus `step` places through the currently available options, wrapping
	 * at both ends. Focus that is not on an option yet — the search input, the
	 * Done button — enters the list at the matching edge.
	 *
	 * @param {HTMLElement}      modal         - The open dropdown modal.
	 * @param {HTMLElement|null} currentOption - The option holding focus, if any.
	 * @param {number}           step          - 1 to move forward, -1 to move back.
	 * @return {void}
	 */
	moveFocus(modal, currentOption, step) {
		const options = this.getAvailableOptions(modal);
		if (options.length === 0) {
			return;
		}

		const currentIndex = currentOption ? options.indexOf(currentOption) : -1;
		if (currentIndex === -1) {
			this.focusOption(step === -1 ? options[options.length - 1] : options[0]);
			return;
		}

		const nextIndex = (currentIndex + step + options.length) % options.length;
		this.focusOption(options[nextIndex]);
	}

	/**
	 * The options a user can currently reach, in DOM order. In-modal search hides
	 * rows with an inline `display` and cross-filtering hides them with a class,
	 * so both have to be skipped.
	 *
	 * @param {HTMLElement} modal - The dropdown modal to read.
	 * @return {HTMLElement[]} The available option rows.
	 */
	getAvailableOptions(modal) {
		return Array.from(modal.querySelectorAll(CONFIG.ELEMENTS.option)).filter(
			(option) => this.isOptionAvailable(option),
		);
	}

	/**
	 * Whether an option row is visible and its input can take focus.
	 *
	 * @param {HTMLElement} option - The option row.
	 * @return {boolean} True when the row can be navigated to.
	 */
	isOptionAvailable(option) {
		if (
			option.classList.contains(CONFIG.CSS_CLASSES.optionHidden) ||
			option.style.display === 'none'
		) {
			return false;
		}

		const input = option.querySelector(CONFIG.ELEMENTS.input);
		return !!input && !input.disabled;
	}

	/**
	 * Focus an option by focusing its input — the row itself is a label, which is
	 * not focusable.
	 *
	 * @param {HTMLElement|undefined} option - The option row to focus.
	 * @return {void}
	 */
	focusOption(option) {
		const input = option?.querySelector(CONFIG.ELEMENTS.input);
		if (!input) {
			return;
		}

		input.focus();

		// The options list is its own scroll container, so keep the row in view.
		if (typeof option.scrollIntoView === 'function') {
			option.scrollIntoView({ block: 'nearest' });
		}
	}
}
